import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import AuthModal from '../components/AuthModal';
import { Mic, Users, Sparkles, Bot, BookOpen, Podcast, MessageCircle, Newspaper, Film, Globe, ChevronDown, Check, Ticket, ShieldCheck, Camera, Languages, Briefcase, Heart } from 'lucide-react';
import { AfricaMapIcon, GuyaneMapIcon } from '../components/icons/MapIcons';
import { Capacitor } from '@capacitor/core';
import { welcomeTexts } from '../utils/welcomeTranslations';
import { useGreenIAStatus } from '../hooks/useGreenIAStatus';
import LegalFooterLinks from '../components/LegalDocModal';

/**
 * Welcome — landing page (v48 redesign).
 *  - Sign-up button is placed BELOW the login button (vertical stack).
 *  - A 13-language picker sits directly under the Sign-up button so visitors
 *    can read the whole page (including the 3 feature cards) in their tongue.
 *  - The old "Commencer maintenant" CTA is removed; tagline shrunk; hero "Tout
 *    pour réussir" rendered slightly smaller per user request.
 */
const Welcome = () => {
  const { user, loading } = useAuth();
  const greenIA = useGreenIAStatus();
  const { t, language, setLanguage, supportedLanguages, languageFlags, languageNames } = useLanguage();
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [showLangMenu, setShowLangMenu] = useState(false);
  const langMenuRef = useRef(null);

  const openLogin = () => { setAuthMode('login'); setShowAuth(true); };
  const openRegister = () => { setAuthMode('register'); setShowAuth(true); };

  // Redirect to the Discover tab (/films) by default when user is already authenticated.
  useEffect(() => {
    if (!loading && user && !showAuth) {
      navigate('/films', { replace: true });
    }
  }, [user, loading, navigate, showAuth]);

  // Close the language menu when clicking outside.
  useEffect(() => {
    const onDown = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setShowLangMenu(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, []);

  const currentLangName = languageNames?.[language]?.native || language.toUpperCase();
  const currentFlag = languageFlags?.[language] || '🌐';
  const W = welcomeTexts(language);
  const [preview, setPreview] = useState({ africa: [], guyane: [] });
  useEffect(() => {
    axios.get('/api/gpt-africa/public-preview', { params: { lang: language } })
      .then((r) => setPreview({ africa: r.data?.africa || [], guyane: r.data?.guyane || [] }))
      .catch(() => {});
  }, [language]);

  // While the session is being restored (token found, validating), never flash the landing page
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #450050 0%, #e753fe 100%)' }} data-testid="welcome-restoring-session">
        <div className="w-10 h-10 border-2 border-white/30 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen text-white overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #450050 0%, #e753fe 100%)' }}
      data-keep-dark=""
    >
      {/* Animated background blobs */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse pointer-events-none"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDelay: '1s' }}></div>

      <div className="relative z-10">
        {/* Header */}
        <header className="container mx-auto px-6 pt-8 pb-4">
          <div className="flex justify-between items-start gap-4">
            <div className="flex items-center space-x-3 pt-2">
              <img src="/destine-logo-transparent.png" alt="Destine IA" className="h-14 sm:h-16" />
            </div>

            {/* Vertical stack: Connexion → Inscription → Language picker */}
            <div className="flex flex-col items-stretch gap-2 w-[170px] sm:w-[200px]">
              <button
                data-testid="welcome-connexion-btn"
                onClick={openLogin}
                className="px-4 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 rounded-full font-medium transition-all duration-300 text-sm sm:text-base"
              >
                {t('welcome.login') || 'Connexion'}
              </button>
              <button
                data-testid="welcome-inscription-btn"
                onClick={openRegister}
                className="px-4 py-2.5 bg-white text-[#450050] hover:bg-white/90 rounded-full font-semibold transition-all duration-300 shadow-md text-sm sm:text-base"
              >
                {t('welcome.register') || "S'inscrire"}
              </button>

              {/* Language picker (under the Sign-up button, as requested) */}
              <div className="relative" ref={langMenuRef}>
                <button
                  data-testid="welcome-language-btn"
                  onClick={() => setShowLangMenu(v => !v)}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 rounded-full text-xs sm:text-sm transition-all"
                  aria-haspopup="listbox"
                  aria-expanded={showLangMenu}
                >
                  <Globe className="w-3.5 h-3.5 opacity-80" />
                  <span className="mr-0.5">{currentFlag}</span>
                  <span className="truncate max-w-[80px]">{currentLangName}</span>
                  <ChevronDown className={`w-3.5 h-3.5 opacity-70 transition-transform ${showLangMenu ? 'rotate-180' : ''}`} />
                </button>
                {showLangMenu && (
                  <div
                    className="absolute right-0 mt-2 w-[210px] max-h-[60vh] overflow-y-auto bg-[#1a0023]/95 backdrop-blur-md border border-white/15 rounded-2xl shadow-2xl py-1.5 z-20"
                    role="listbox"
                    data-testid="welcome-language-menu"
                  >
                    {supportedLanguages.map((code) => (
                      <button
                        key={code}
                        role="option"
                        aria-selected={language === code}
                        data-testid={`welcome-language-option-${code}`}
                        onClick={() => { setLanguage(code); setShowLangMenu(false); }}
                        className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-sm hover:bg-white/10 transition-colors ${language === code ? 'text-white' : 'text-white/85'}`}
                      >
                        <span className="flex items-center gap-2 truncate">
                          <span className="text-base leading-none">{languageFlags?.[code] || '🌐'}</span>
                          <span className="truncate">{languageNames?.[code]?.native || code.toUpperCase()}</span>
                        </span>
                        {language === code && <Check className="w-4 h-4 text-emerald-300 shrink-0" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Hero */}
        <main className="container mx-auto px-6 pb-20">
          <div className="max-w-4xl mx-auto text-center space-y-8 pt-10 sm:pt-16">
            <div className="space-y-4 fade-in">
              {/* "Tout pour réussir" must stay on a single line per user request */}
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight whitespace-nowrap">
                {t('welcome.title') || 'Tout pour réussir'}
              </h2>
              <p className="text-base sm:text-lg lg:text-xl text-white/85 max-w-2xl mx-auto leading-relaxed">
                {t('welcome.tagline') || 'Destine est le premier réseau social audiovisuel au monde'}
              </p>
            </div>

            {/* En savoir plus only (Commencer maintenant removed per user request) */}
            <div className="flex justify-center items-center pt-2">
              <a
                href="https://info.destine.studio"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="welcome-learnmore-btn"
                className="px-7 py-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 rounded-full font-semibold text-base transition-all duration-300 inline-block"
              >
                {t('welcome.learnMore') || 'En savoir plus'}
              </a>
            </div>

            {/* Feature showcase — v50.15 redesign (6 focused cards, incl. Green IA) */}
            <div className="grid md:grid-cols-12 gap-5 sm:gap-6 pt-16 text-left">
              {/* Destine IA — large */}
              <div className="md:col-span-6 p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-indigo-600/30 via-violet-700/25 to-black/30 border border-white/20 backdrop-blur-sm hover:border-indigo-300/50 transition-all" data-testid="welcome-card-ia">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-2xl flex items-center justify-center shadow-lg shrink-0">
                    <Bot className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold">{W.iaTitle}</h3>
                </div>
                <p className="text-white/80 text-sm sm:text-base mb-3">{W.iaDesc}</p>
                <div className="flex items-start gap-3 p-3 mb-4 rounded-2xl bg-fuchsia-500/15 border border-fuchsia-400/40" data-testid="welcome-deesse-line">
                  <img src="/app-icon.png" alt="Déesse" className="w-10 h-10 rounded-full border border-fuchsia-300/60 shrink-0" />
                  <p className="text-fuchsia-100 text-xs sm:text-sm leading-relaxed">{W.deesseLine}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1.5 rounded-full bg-fuchsia-500/25 border border-fuchsia-300/40 text-xs flex items-center gap-1.5" data-testid="welcome-chip-deesse"><Heart className="w-3.5 h-3.5" />{W.iaC4}</span>
                  <span className="px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs flex items-center gap-1.5"><Mic className="w-3.5 h-3.5" />{W.iaC1}</span>
                  <span className="px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs flex items-center gap-1.5"><Languages className="w-3.5 h-3.5" />{W.iaC2}</span>
                  <span className="px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" />{W.iaC3}</span>
                </div>
              </div>

              {/* v50.10.9 — GPT Africa (commercial) + aperçu des langues */}
              <div className="md:col-span-6 p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-amber-600/30 via-emerald-700/25 to-black/30 border border-white/20 backdrop-blur-sm hover:border-amber-300/50 transition-all" data-testid="welcome-card-gpt-africa">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-14 h-14 bg-gradient-to-br from-white/90 to-pink-100 rounded-2xl flex items-center justify-center shadow-lg shrink-0">
                    <AfricaMapIcon className="w-9 h-9" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold">{W.africaTitle}</h3>
                </div>
                <p className="text-white/85 text-sm sm:text-base mb-4">{W.africaDesc}</p>
                <label className="block text-xs text-white/70 mb-1.5">{W.africaPick}</label>
                <select
                  className="w-full bg-black/40 border border-white/20 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-amber-300/60"
                  defaultValue=""
                  onChange={openRegister}
                  data-testid="welcome-africa-lang-select"
                >
                  <option value="" disabled>{W.africaPick} — {preview.africa.length}</option>
                  {preview.africa.map((l) => (
                    <option key={l.id} value={l.id}>{l.flag ? `${l.flag} ` : ''}{l.label}</option>
                  ))}
                </select>
                <div className="flex items-center justify-between gap-3 mt-4">
                  <span className="text-xs text-white/60">{W.africaSoon}</span>
                  <button onClick={openRegister} className="px-4 py-2 rounded-full bg-white text-[#450050] text-sm font-bold hover:bg-white/90 transition-colors" data-testid="welcome-africa-cta">{W.aiCta}</button>
                </div>
              </div>

              {/* v50.10.9 — GPT Guyane (commercial) */}
              <div className="md:col-span-6 p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-emerald-600/30 via-teal-700/25 to-black/30 border border-white/20 backdrop-blur-sm hover:border-emerald-300/50 transition-all" data-testid="welcome-card-gpt-guyane">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-14 h-14 bg-gradient-to-br from-white/90 to-pink-100 rounded-2xl flex items-center justify-center shadow-lg shrink-0">
                    <GuyaneMapIcon className="w-9 h-9" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold">{W.guyaneTitle}</h3>
                </div>
                <p className="text-white/85 text-sm sm:text-base mb-4">{W.guyaneDesc}</p>
                <div className="flex flex-wrap gap-2 mb-4" data-testid="welcome-guyane-langs">
                  {preview.guyane.map((l) => (
                    <span key={l.id} className="px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs">{l.label}</span>
                  ))}
                </div>
                <div className="flex justify-end">
                  <button onClick={openRegister} className="px-4 py-2 rounded-full bg-white text-[#450050] text-sm font-bold hover:bg-white/90 transition-colors" data-testid="welcome-guyane-cta">{W.aiCta}</button>
                </div>
              </div>

              {/* Voice network — large */}
              <div className="md:col-span-6 p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-fuchsia-600/30 via-purple-700/25 to-black/30 border border-white/20 backdrop-blur-sm hover:border-fuchsia-300/50 transition-all" data-testid="welcome-card-voice">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-fuchsia-600 rounded-2xl flex items-center justify-center shadow-lg shrink-0">
                    <Mic className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold">{W.voiceTitle}</h3>
                </div>
                <p className="text-white/80 text-sm sm:text-base mb-4">{W.voiceDesc}</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs flex items-center gap-1.5"><Podcast className="w-3.5 h-3.5" />{W.voiceC1}</span>
                  <span className="px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" />{W.voiceC2}</span>
                  <span className="px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs flex items-center gap-1.5"><Camera className="w-3.5 h-3.5" />{W.voiceC3}</span>
                </div>
              </div>

              {/* MyTalk — large (v60.1) */}
              <div className="md:col-span-6 p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-sky-600/30 via-blue-700/25 to-black/30 border border-white/20 backdrop-blur-sm hover:border-sky-300/50 transition-all" data-testid="welcome-card-talk">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-14 h-14 bg-gradient-to-br from-sky-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shrink-0">
                    <MessageCircle className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold">{W.talkTitle}</h3>
                </div>
                <p className="text-white/85 text-sm sm:text-base">{W.talkDesc}</p>
              </div>

              {/* Destine Ads — Pro (v60.1) */}
              <div className="md:col-span-6 p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-orange-600/30 via-rose-700/25 to-black/30 border border-white/20 backdrop-blur-sm hover:border-orange-300/50 transition-all" data-testid="welcome-card-ads">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-rose-600 rounded-2xl flex items-center justify-center shadow-lg shrink-0">
                    <Briefcase className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold">{W.adsTitle}</h3>
                    <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-white/15 border border-white/25 text-[11px] font-semibold uppercase tracking-wide">{W.adsTag}</span>
                  </div>
                </div>
                <p className="text-white/85 text-sm sm:text-base">{W.adsDesc}</p>
              </div>

              {/* Cinema */}
              <div className="md:col-span-3 p-5 sm:p-6 rounded-3xl bg-gradient-to-br from-amber-600/25 to-orange-900/25 border border-white/20 backdrop-blur-sm hover:border-amber-300/50 transition-all" data-testid="welcome-card-cinema">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                  <Film className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold mb-2">{W.cinemaTitle}</h3>
                <p className="text-white/75 text-xs sm:text-sm leading-relaxed">{W.cinemaDesc}</p>
              </div>

              {/* Services */}
              <div className="md:col-span-3 p-5 sm:p-6 rounded-3xl bg-gradient-to-br from-rose-600/25 to-pink-900/25 border border-white/20 backdrop-blur-sm hover:border-rose-300/50 transition-all" data-testid="welcome-card-services">
                <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                  <Globe className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold mb-2">{W.servicesTitle}</h3>
                <p className="text-white/75 text-xs sm:text-sm leading-relaxed">{W.servicesDesc}</p>
              </div>
              {/* Green IA — masquée si cédée (v50.10.8) */}
              {greenIA.enabled && (
              <div className="md:col-span-3 p-5 sm:p-6 rounded-3xl bg-gradient-to-br from-emerald-600/30 to-green-900/30 border border-emerald-400/30 backdrop-blur-sm hover:border-emerald-300/60 transition-all" data-testid="welcome-card-green-ia">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold mb-2">{W.greenTitle}</h3>
                <p className="text-white/75 text-xs sm:text-sm leading-relaxed">{W.greenDesc}</p>
                <img
                  src={`/api/green-ia/ap-ia/label.svg?lang=${language}`}
                  alt="Label Green IA"
                  className="mt-4 h-12 opacity-90"
                  data-testid="welcome-green-ia-label"
                  loading="lazy"
                />
              </div>)}

            </div>

            {/* Download App — web only */}
            {!Capacitor.isNativePlatform() && (
              <div className="pt-14 text-center">
                <p className="text-white/60 text-sm mb-4">{t('welcome.mobileAvailable') || 'Disponible sur mobile'}</p>
                <div className="flex gap-4 justify-center items-center">
                  <a
                    href="https://apps.apple.com/fr/app/destine-studio/id6756779175"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2.5 px-5 py-3 bg-black/40 hover:bg-black/60 border border-white/15 rounded-xl transition-all"
                    data-testid="download-appstore-btn"
                  >
                    <svg viewBox="0 0 384 512" fill="white" className="w-5 h-5"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" /></svg>
                    <div className="text-left">
                      <div className="text-[10px] text-white/60 leading-tight">{t('welcome.downloadOn') || 'Télécharger sur'}</div>
                      <div className="text-sm font-semibold leading-tight">App Store</div>
                    </div>
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=app.destine.studio"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2.5 px-5 py-3 bg-black/40 hover:bg-black/60 border border-white/15 rounded-xl transition-all"
                    data-testid="download-playstore-btn"
                  >
                    <svg viewBox="0 0 512 512" fill="white" className="w-5 h-5"><path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" /></svg>
                    <div className="text-left">
                      <div className="text-[10px] text-white/60 leading-tight">{t('welcome.availableOn') || 'Disponible sur'}</div>
                      <div className="text-sm font-semibold leading-tight">Google Play</div>
                    </div>
                  </a>
                </div>
              </div>
            )}
          </div>
        </main>
        {/* v50.10.8c — CGU / CGV ouvertes dans l'app (modale fermable) */}
        <footer className="pb-8 pt-2 px-4">
          <LegalFooterLinks />
        </footer>
      </div>

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} initialMode={authMode} />}
    </div>
  );
};

export default Welcome;
